import React from "react"
import { Stack, Typography, Box, Chip } from "@mui/material"

import { getPSEColorByStatus, getPSEStatusLabel, getPSESubStatusLabel, isPSEInsufficientWeight } from "../../../utils/productionStepExecution"
import { getTransformationTypeLabel } from "../../../utils/supplierItemUtils"

import CommentsIconBadge from "../../../components/Comments/CommentsIcon"
import MergedPSEHeaderInfos from "./MergedPSEHeaderInfos"
import { COLORS } from "../../../utils"
import PSEStatusChip from "./PSEStatusChip"

const spacing = 16 / 6

const sx = {
	container: {
		position: "relative",
		display: "flex",
		padding: "16px",
		borderRadius: "8px",
		flexDirection: "column",
		gap: "24px",
		alignSelf: "stretch",
		// Important: to add margin below the fixed header
		marginTop: "24px",
	},
	title: {
		fontSize: 20,
		fontWeight: 500,
		color: "#262626",
		lineHeight: 1.4,
	},
	label: {
		fontSize: 16,
		fontWeight: 400,
		color: "#414141",
		lineHeight: 1.5
	},
	value: {
		fontSize: 18,
		fontWeight: 400,
		color: "#262626",
		lineHeight: 1.5
	},
	commentsIconContainer: {
		position: "absolute",
		right: 24,
		top: 12,
		color: "rgba(85 85 85)",
		display: "flex",
		alignItems: "center",
		gap: 1
	},
	chip: {
		height: 24,
		fontWeight: 500,
		fontSize: 14,
		marginLeft: "16px"
	},
	reprodChip: {
		fontSize: 14,
		lineHeight: 1,
		fontWeight: 400,
		color: COLORS.GREEN_800,
		backgroundColor: COLORS.GREEN_50,
		marginLeft: "16px",
		border: "1px solid #86c89aff",
	},
	transformationChip: {
		display: "flex",
		alignItems: "center",
		gap: "6px",
		padding: "4px 8px",
		fontSize: 16,
		fontWeight: 400,
		lineHeight: 1.5,
		border: "1px solid #555",
		backgroundColor: "#FFFFFF",
		borderRadius: "99999px"
	}
}

const PSEHeaderResume = ({
	onClickComments = null,
	commentsCount = 0,
	renderPrint,
	productionStepExecution,
	style = {}
}) => {
	const isReprod = !!productionStepExecution.original
	const isOriginalForReprod = !!productionStepExecution.reproduction

	const palette = getPSEColorByStatus(productionStepExecution.status)

	return (
		<Box sx={{ ...sx.container, backgroundColor: palette.main, ...style }}>
			{/* top */}
			<Box className="flexRow center spaceBetween">
				<Stack spacing={spacing} direction="row" alignItems="center" sx={{ width: "80%" }}>
					<Typography variant="h1" sx={sx.title}>
						{productionStepExecution.productionStepSnapshot.name}
						{isReprod && (
							<Chip label="Reprod" color="success" sx={sx.reprodChip} variant="filled" />
						)}
						<PSEStatusChip
							statusValue={productionStepExecution.status}
							statusName={getPSEStatusLabel(productionStepExecution.status)}
							sx={{ display: "inline-flex", marginLeft: "16px" }}
						/>
						{(isPSEInsufficientWeight(productionStepExecution.subStatus) && !isOriginalForReprod) && (
							<Chip label={getPSESubStatusLabel(productionStepExecution.subStatus)} color="error" sx={sx.chip} variant="filled" />
						)}
					</Typography>
				</Stack>
				<Box sx={sx.commentsIconContainer}>
					{onClickComments && <CommentsIconBadge onToggle={onClickComments} count={commentsCount} />}
					{renderPrint && (
						<Box sx={{ mt: 1.1 }}>
							{renderPrint()}
						</Box>
					)}
				</Box>
			</Box>
			{/* bottom */}
			<Stack spacing={4} direction="row">
				{/* transformation */}
				<Stack spacing={1}>
					<Typography sx={sx.label}>
						Type d’étape
					</Typography>
					<Box sx={sx.transformationChip}>
						<Typography>
							{getTransformationTypeLabel(productionStepExecution.productionStepSnapshot.transformation)}
						</Typography>
						<img
							alt={getTransformationTypeLabel(productionStepExecution.productionStepSnapshot.transformation)}
							src={"/icons/pses/transformations/" + productionStepExecution.productionStepSnapshot.transformation.toLowerCase() + ".svg"}
						/>
					</Box>
				</Stack>
				{/* section */}
				<Stack spacing={1}>
					<Typography sx={sx.label}>
						Section
					</Typography>
					{productionStepExecution.isStepMerged
						? (
							<MergedPSEHeaderInfos type="sections" mergedProductionStepExecutions={productionStepExecution.mergedProductionStepExecutions} />
						)
						: (
							<Typography sx={sx.value}>
								{productionStepExecution.sectionName}
							</Typography>
						)}

				</Stack>
				{/* recipe */}
				<Stack spacing={1}>
					<Typography sx={sx.label}>
						Recette
					</Typography>
					{productionStepExecution.isStepMerged
						? (
							<MergedPSEHeaderInfos type="recipes" mergedProductionStepExecutions={productionStepExecution.mergedProductionStepExecutions} />
						)
						: (
							<Typography sx={sx.value}>
								{productionStepExecution.uniqueCode} - {productionStepExecution.recipeName}
							</Typography>
						)}
				</Stack>
			</Stack>
		</Box>
	)
}

export default PSEHeaderResume